import { useEffect, useState } from 'react';
import { INps, useNps } from '../../hooks/nps';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { Button as ButtonComponent } from '../../../../components/Button';

interface IProps {
  open: boolean;
  nps: INps | null;
  onClose: () => void;
  onEmailUpdated: () => void;
}

export const NpsResendForm: React.FC<IProps> = (props) => {
  const { updateNpsEmail, resendNps } = useNps();
  const { enqueueSnackbar } = useSnackbar();

  const [email, setEmail] = useState('');
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);

  const originalEmail = props.nps?.order?.customer.email ?? '';
  const emailChanged = email.trim() !== originalEmail.trim();

  useEffect(() => {
    if (props.open) {
      setEmail(props.nps?.order?.customer.email ?? '');
    }
  }, [props.open, props.nps]);

  const saveEmail = async () => {
    if (!props.nps) return false;

    const response = await updateNpsEmail(props.nps.id, { email });

    enqueueSnackbar(response.message, {
      variant: response.status === 200 ? 'success' : 'warning',
    });

    if (response.status === 200) {
      props.onEmailUpdated();
      return true;
    }

    return false;
  };

  const handleSave = async () => {
    setLoadingSave(true);

    try {
      await saveEmail();
    } catch (error) {
      enqueueSnackbar('Erro ao atualizar o e-mail!', { variant: 'error' });
    } finally {
      setLoadingSave(false);
    }
  };

  const handleResend = async () => {
    if (!props.nps) return;

    setLoadingResend(true);

    try {
      if (emailChanged) {
        const saved = await saveEmail();

        if (!saved) return;
      }

      const response = await resendNps(props.nps.id);

      enqueueSnackbar(response.message, {
        variant: response.status === 200 ? 'success' : 'warning',
      });

      if (response.status === 200) {
        props.onClose();
      }
    } catch (error) {
      enqueueSnackbar('Erro ao reenviar a pesquisa!', { variant: 'error' });
    } finally {
      setLoadingResend(false);
    }
  };

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={props.open}
      onClose={props.onClose}
      aria-labelledby="resend-dialog-title"
    >
      <DialogTitle id="resend-dialog-title">Reenviar NPS</DialogTitle>
      <DialogContent>
        <DialogContentText style={{ marginBottom: 16 }}>
          Pedido: {props.nps?.order?.number}
          <Typography gutterBottom>
            {props.nps?.order?.customer.name}
          </Typography>
        </DialogContentText>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={2}
              label="E-mail(s) do cliente"
              helperText="Para mais de um destinatário, separe os e-mails com ;"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={props.onClose} color="inherit">
          Fechar
        </Button>
        <ButtonComponent
          variant="outlined"
          color="secondary"
          onClick={handleSave}
          loading={loadingSave}
          disabled={!emailChanged || loadingResend}
        >
          Salvar e-mail
        </ButtonComponent>
        <ButtonComponent
          variant="contained"
          color="secondary"
          onClick={handleResend}
          loading={loadingResend}
          disabled={loadingSave}
        >
          Reenviar
        </ButtonComponent>
      </DialogActions>
    </Dialog>
  );
};
